// src/lib/systemNotify.js
//
// Browser/OS notifications. A person who's clocked in is usually working in
// another window, so an in-page message would never be seen — a desktop
// notification still shows. Everything here fails quietly: no permission, or
// a browser without notifications, just means no popup.
//
// Permission is only ever asked for from a button the person presses (see the
// tracker page). Asking in the middle of clocking in collided with the
// screen-share prompt, and a prompt that gets dismissed is never shown again.

const ICON = "https://techbugz.com/wp-content/uploads/2026/04/tech-bugz-icon-new.png";

// A real function, not just a key that exists — some embedded browsers define it as undefined.
export const canNotify = () => typeof window !== "undefined" && typeof window.Notification === "function";

// "granted" | "denied" | "default" (never asked / dismissed) | "unsupported"
export const notificationStatus = () => (canNotify() ? window.Notification.permission : "unsupported");

// Shows the browser's permission prompt (from a click) and reports the answer.
// Safe to call any time — it doesn't re-prompt once answered.
export async function askNotificationPermission() {
  if (!canNotify()) return "unsupported";
  if (window.Notification.permission !== "default") return window.Notification.permission;
  try {
    const answer = await window.Notification.requestPermission();
    return answer || window.Notification.permission;
  } catch {
    return window.Notification.permission; // old Safari signature, or blocked
  }
}

// Returns the notification if the browser accepted it, else null. (It can still be hidden by the
// system — Focus Assist / Do Not Disturb — which no web page can see.)
function show(title, options) {
  if (!canNotify() || window.Notification.permission !== "granted") return null;
  try {
    return new window.Notification(title, { icon: ICON, silent: true, ...options });
  } catch {
    return null; // some mobile browsers only allow notifications via a service worker
  }
}

export const sendTestNotification = () => !!show("Desktop alerts are on", { body: "You'll see one of these each time a screenshot is taken.", tag: "tb-test" });

// One notification per screenshot. Same tag, so the newest replaces the
// last in the notification centre, but `renotify` makes every one pop up and
// play the system sound again (a replaced notification is silent otherwise).
export function notifyScreenshot({ project, at = new Date(), offline = false }) {
  const time = at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return !!show(offline ? "Screenshot saved offline" : "Screenshot captured", {
    body: `${offline ? "Will upload when you're back online · " : ""}${project ? `${time} · ${project}` : time}`,
    tag: "tb-screenshot",
    renotify: true,
    silent: false,
  });
}
